import { useCallback, useRef } from 'react';
import { isValidFokusClipboard } from './clipboardUtils';
import { duplicateTasksDirectWithLoad } from './clipboardUtils/directDuplicate';
import { showBusyOverlay } from '../../../BusyOverlay';
import { copyTasksToClipboard } from './clipboardActions/copyTasksToClipboard';
import { pasteClipboardData } from './clipboardActions/pasteClipboardData';
import { clipboardHasFokusData, readClipboardPayload } from './clipboardActions/readClipboardPayload';
import { isLargeOperation } from './clipboardActions/clipboardSize';
import { useClipboardShortcuts } from './clipboardActions/useClipboardShortcuts';

/**
 * useClipboard Hook
 *
 * Handles copy/paste operations for flowchart nodes using the system clipboard.
 * - Ctrl+C copies selected nodes to system clipboard as JSON
 * - Ctrl+V pastes from system clipboard (if valid Fokus format)
 * - Ctrl+V pastes then tidies; Paste Here / (+) Paste Mission hold drop coords until the next tidy
 * - Deep copies nested sprint references (creates new IDs)
 * - Works across tabs, windows, and page refreshes
 *
 * See clipboardUtils.js for the JSON format specification.
 */
export function useClipboard({
  tasks,
  selectedNodes,
  setSelectedNodes,
  nodePositions,
  nestedSprints,
  allTags = [],
  customFields = [],
  onPasteTasks,
  onDeleteTasks,
  onAfterDelete = null,
  isMultiCampaignMode = false,
  openCampaignPickerForPaste,
  loadFullSprint,
  allSprints = [],
  findEmptySpaceNearNode = null,
}) {
  const lastCopiedRef = useRef(null);
  // Huge nested pastes take seconds. Keyboard repeat / double Ctrl+V must not
  // start a second paste — that creates duplicate nested campaigns.
  const clipboardOpInFlightRef = useRef(false);

  const withClipboardLock = useCallback(async (fn) => {
    if (clipboardOpInFlightRef.current) {
      console.warn('[CLIPBOARD] Ignoring overlapping copy/paste/duplicate');
      return false;
    }
    clipboardOpInFlightRef.current = true;
    try {
      return await fn();
    } finally {
      clipboardOpInFlightRef.current = false;
    }
  }, []);

  const rememberCopy = useCallback((clipboardData) => {
    lastCopiedRef.current = clipboardData;
  }, []);

  const copySelectedNodes = useCallback(async () => {
    if (selectedNodes.size === 0) return false;
    const selectedIds = Array.from(selectedNodes);
    const selectedTasks = tasks.filter((task) => selectedIds.includes(task.id));
    if (selectedTasks.length === 0) return false;

    return withClipboardLock(() => copyTasksToClipboard({
      tasksToCopy: selectedTasks,
      nodePositions,
      nestedSprints,
      allSprints,
      loadFullSprint,
      allTags,
      customFields,
      busyMessage: 'Copying campaigns…',
      successLog: `📋 Copied ${selectedTasks.length} node(s) to clipboard (JSON format)`,
      remember: rememberCopy,
    }));
  }, [selectedNodes, tasks, nodePositions, nestedSprints, allTags, customFields, allSprints, loadFullSprint, withClipboardLock, rememberCopy]);

  const pasteNodesCore = useCallback(async (clipboardDataOverride = null, placement = null) => {
    if (!onPasteTasks) return false;

    return withClipboardLock(async () => {
      const clipboardData = await readClipboardPayload(clipboardDataOverride, lastCopiedRef.current);
      if (!isValidFokusClipboard(clipboardData)) {
        console.log('📋 Clipboard does not contain valid Fokus data');
        return false;
      }
      return pasteClipboardData({
        clipboardData,
        placement,
        onPasteTasks,
        setSelectedNodes,
      });
    });
  }, [onPasteTasks, setSelectedNodes, withClipboardLock]);

  const pasteNodes = useCallback(async (clipboardDataOverride = null) => {
    if (isMultiCampaignMode && openCampaignPickerForPaste) {
      const clipboardData = await readClipboardPayload(clipboardDataOverride, lastCopiedRef.current);
      if (!isValidFokusClipboard(clipboardData)) {
        console.log('📋 Clipboard does not contain valid Fokus data');
        return false;
      }
      return openCampaignPickerForPaste(clipboardData);
    }
    return pasteNodesCore(clipboardDataOverride, null);
  }, [pasteNodesCore, isMultiCampaignMode, openCampaignPickerForPaste]);

  const pasteAtPosition = useCallback(async (position) => {
    return pasteNodesCore(null, {
      targetPosition: position,
      skipAutoLayout: true,
    });
  }, [pasteNodesCore]);

  const pasteConnectedToHost = useCallback(async (hostTaskId, branch = null) => {
    if (!hostTaskId) return false;
    const origin = findEmptySpaceNearNode?.(hostTaskId) || null;
    return pasteNodesCore(null, {
      targetPosition: origin,
      attachToHostId: hostTaskId,
      attachBranch: branch,
      skipAutoLayout: true,
    });
  }, [pasteNodesCore, findEmptySpaceNearNode]);

  const pasteBeforeHost = useCallback(async (hostTaskId) => {
    if (!hostTaskId) return false;
    const host = tasks.find((task) => task.id === hostTaskId);
    if (!host) return false;
    const origin = {
      x: typeof host.x === 'number' ? host.x : 100,
      y: (typeof host.y === 'number' ? host.y : 40) - 72,
    };
    return pasteNodesCore(null, {
      targetPosition: origin,
      insertBeforeHost: host,
      skipAutoLayout: true,
    });
  }, [pasteNodesCore, tasks]);

  const copyNode = useCallback(async (taskId) => {
    const task = tasks.find((item) => item.id === taskId);
    if (!task) return false;

    return copyTasksToClipboard({
      tasksToCopy: [task],
      nodePositions: { [taskId]: { x: 0, y: 0 } },
      nestedSprints,
      allSprints,
      loadFullSprint,
      allTags,
      customFields,
      busyMessage: 'Copying campaign…',
      successLog: `📋 Copied "${task.name}" to clipboard (JSON format)`,
      remember: rememberCopy,
    });
  }, [tasks, nestedSprints, allTags, customFields, allSprints, loadFullSprint, rememberCopy]);

  const duplicateNodesDirect = useCallback(async (taskIds) => {
    if (!onPasteTasks || !taskIds || taskIds.length === 0) return;

    const sourceTasks = tasks.filter((task) => taskIds.includes(task.id));
    return withClipboardLock(async () => {
      const hideBusy = isLargeOperation(sourceTasks)
        ? showBusyOverlay('Duplicating campaigns…')
        : null;
      try {
        const result = await duplicateTasksDirectWithLoad(
          taskIds,
          tasks,
          nodePositions,
          nestedSprints,
          allSprints,
          loadFullSprint,
        );
        if (!result) return false;

        await onPasteTasks({
          tasks: result.tasks,
          positions: {},
          nestedSprints: result.nestedSprints,
          tagDefinitions: [],
          customFieldDefinitions: [],
        });

        if (setSelectedNodes) {
          setSelectedNodes(new Set(result.tasks.map((task) => task.id)));
        }

        console.log(`📋 Duplicated ${result.tasks.length} node(s) directly`);
        return true;
      } finally {
        hideBusy?.();
      }
    });
  }, [tasks, nodePositions, nestedSprints, allSprints, loadFullSprint, onPasteTasks, setSelectedNodes, withClipboardLock]);

  const duplicateNode = useCallback(async (taskId) => {
    await duplicateNodesDirect([taskId]);
  }, [duplicateNodesDirect]);

  useClipboardShortcuts({
    selectedNodes,
    copySelectedNodes,
    pasteNodes,
    duplicateNodesDirect,
    onDeleteTasks,
    onAfterDelete,
  });

  const checkClipboardData = useCallback(
    () => clipboardHasFokusData(lastCopiedRef.current),
    [],
  );

  return {
    copySelectedNodes,
    copyNode,
    pasteNodes,
    pasteAtPosition,
    pasteConnectedToHost,
    pasteBeforeHost,
    duplicateNode,
    hasClipboardData: checkClipboardData,
  };
}

export default useClipboard;
